import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-status-pill>` says about itself.
 *
 * StatusPill and `<caos-tag>` are two components ON PURPOSE, and the reason is worth stating once
 * because most libraries ship one. A pill is LIFECYCLE STATE: where a record is in its life, one
 * value at a time, drawn from a vocabulary the object defines — Draft, In review, Approved, Closed.
 * A tag is an OPERATIONAL LABEL: something a person put on the record, any number of them, from no
 * vocabulary at all — Rush, Repeat customer, Needs follow-up.
 *
 * Where they are one component, the difference has to be encoded in a colour by convention, and
 * then green means both "approved" and "somebody typed the word priority". That convention lives in
 * an administrator's head, is applied unevenly across objects, and cannot be validated by anything.
 * Two components put the distinction in the markup, where the object model can see it and a reader
 * can tell what they are looking at without knowing the local convention. The `beside-a-tag`
 * example below is the two of them side by side.
 *
 * A note on the shape: the pill has NO `observedAttributes` and no script at all — `intent` and
 * `dot` are read by the stylesheet, off `:host([intent="…"])`. It behaves the same (a change takes
 * effect immediately, and CSS never has to be told to re-run), but it means the intent is not
 * validated: an unrecognised value simply falls through to the default treatment rather than being
 * corrected or complained about.
 *
 * On the default: the source declares no default `intent`, but the base `.pill` rule paints the
 * muted colour, so an untinted pill and `intent="muted"` draw identically. That is recorded as the
 * default below because it is what an author gets, not because the source names one.
 */
const docs: ComponentDocs = {
  aliases: ['badge', 'chip', 'state badge', 'lozenge', 'status badge', 'label'],

  answers: [
    'show where a record is in its life',
    'show that a request has been approved or rejected',
    'mark a row as a draft',
    'show the state of every row in a list at a glance',
    'flag a non-production environment',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['info', 'success', 'warning', 'danger', 'muted', 'dev'],
      description:
        'The tint: info, success, warning, danger, muted or dev. It colours the lettering and the 1px ' +
        'edge — there is no filled chip, so the colour never has to be legible against itself. An ' +
        'unrecognised value draws the muted treatment.',
      default: 'muted',
    },
    {
      name: 'dot',
      attribute: 'dot',
      type: 'boolean',
      description:
        'Put a 6px dot ahead of the word, in the pill\'s own colour. Useful in a dense list where the ' +
        'lettering is small enough that the tint alone is easy to miss.',
      default: false,
    },
  ],

  // Read from the source: the pill has no script. It draws what it is told and reports nothing.
  events: [],

  state: [],

  slots: [{ name: '', takes: 'The state name. One or two words — it is set in 9px tracked capitals.' }],

  tokens: [
    { token: '--caos-color-info', controls: 'The lettering and edge of an info pill.' },
    { token: '--caos-color-success', controls: 'The lettering and edge of a success pill.' },
    { token: '--caos-color-warning', controls: 'The lettering and edge of a warning pill.' },
    { token: '--caos-color-danger', controls: 'The lettering and edge of a danger pill.' },
    { token: '--caos-color-text-muted', controls: 'The lettering and edge of a muted pill, which is also the untinted default.' },
    { token: '--caos-color-accent-strong', controls: 'The lettering and edge of a dev pill — the one intent that moves with a tenant\'s brand.' },
    { token: '--caos-font-mono', controls: 'The typeface the state name is set in.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the state name.' },
  ],

  parts: [
    { part: 'pill', is: 'the pill' },
    { part: 'dot', is: 'the dot inside it' },
  ],

  methods: [],

  placing:
    'Inline, beside the thing whose state it reports — after a record name in a header, in the state ' +
    'column of a list, at the end of a configuration row. It is `inline-flex` and vertically centred, ' +
    'so it sits on a line of text without pushing it around, and it never wraps. One per record: two ' +
    'pills beside one name is a sign that one of them is really a tag.',

  whenToUse:
    'The single lifecycle state of a record or a configuration row, where the state comes from a fixed ' +
    'vocabulary and only one of them is true at a time.',

  insteadUse: [
    {
      name: 'Tag',
      when:
        'The word was typed by a person rather than chosen by the object\'s lifecycle. That is the ' +
        'whole line between these two: a pill answers "what state is this record in", one value from a ' +
        'closed vocabulary; a tag answers "what did somebody label this", any number of values from no ' +
        'vocabulary at all. If a record can carry two of them at once, or if the words vary by who ' +
        'typed them, it is a tag. Using a pill for a free-form label spends the state colours on ' +
        'something that has no state, and after that nothing on the row can be read at a glance.',
    },
    {
      name: 'StatusDot',
      when:
        'What is being reported is the health of a service or an integration rather than the state of ' +
        'a record — connected, degraded, down. A dot says it in nine pixels and needs no word.',
    },
    {
      name: 'ProvenanceMarker',
      when:
        'The subject is one VALUE and where it came from — computed, overridden, synced — rather than ' +
        'the record and where it has got to.',
    },
    {
      name: 'NoticeBanner',
      when:
        'The state needs explaining rather than naming. A pill is one or two words; anything that needs ' +
        'a sentence and a decision is a banner.',
    },
  ],

  accessibility:
    'The state is TEXT, and that is the accessibility story: the word is read out as ordinary content, ' +
    'the colour only reinforces it, and the optional dot is hidden from assistive tech because it says ' +
    'nothing the word does not. No role is set, deliberately — a pill is a piece of the sentence it ' +
    'sits in, not a control or a live region. Left to the author: keeping the word, since a pill drawn ' +
    'with a dot and no label would be colour alone; and keeping the vocabulary short, because the ' +
    'lettering is 9px capitals and long states are what force a state column wider than the name ' +
    'column beside it.',

  examples: [
    {
      key: 'lifecycle',
      label: 'A lifecycle vocabulary',
      summary:
        'One object\'s states in the order a record passes through them, so the tints can be assigned ' +
        'to a vocabulary as a set rather than one pill at a time.',
      placement: [
        { component: 'caos-status-pill', inputs: { intent: 'muted' }, children: ['Draft'] },
        { component: 'caos-status-pill', inputs: { intent: 'info' }, children: ['In review'] },
        { component: 'caos-status-pill', inputs: { intent: 'warning' }, children: ['Awaiting approval'] },
        { component: 'caos-status-pill', inputs: { intent: 'success' }, children: ['Approved'] },
        { component: 'caos-status-pill', inputs: { intent: 'danger' }, children: ['Rejected'] },
      ],
    },
    {
      key: 'beside-a-tag',
      label: 'A state pill beside a label tag',
      summary:
        'The distinction the library is drawing, made visible: the pill on the left is the one state ' +
        'the record is in, and the two chips beside it are labels people put on it. They are different ' +
        'components because they answer different questions.',
      placement: [
        { component: 'caos-status-pill', inputs: { intent: 'success' }, children: ['Approved'] },
        { component: 'caos-tag', children: ['Rush'] },
        { component: 'caos-tag', children: ['Repeat customer'] },
      ],
    },
    {
      key: 'dot',
      label: 'With a leading dot',
      summary:
        'The same states with the optional dot, which is what a dense list wants — the tint reads from ' +
        'further away than 9px lettering does.',
      placement: [
        { component: 'caos-status-pill', inputs: { intent: 'success', dot: true }, children: ['Closed'] },
        { component: 'caos-status-pill', inputs: { intent: 'warning', dot: true }, children: ['Blocked'] },
        { component: 'caos-status-pill', inputs: { intent: 'danger', dot: true }, children: ['Cancelled'] },
        { component: 'caos-status-pill', inputs: { intent: 'dev', dot: true }, children: ['Dev'] },
      ],
    },
  ],
};

export default docs;
